// Peace Love Wellness — self-pay fees (single source of truth)
//
// Every published fee derives from FEES: /insurance-fees (cards, FAQ, structured
// data), the site FAQ, the home FAQ, the service catalog in utils/schema.js,
// and clinician cards/profiles (via utils/disclosure.js).
//
// Sliding-scale rates are ALWAYS published as a range (floor to full fee),
// never a single number: where someone lands depends on their circumstances.
//
// Hand-written copies that can't import this module — static/llms.txt,
// static/practice-facts.json, and two sentences in src/data/blog.js (loaded by
// gatsby-node via require) — are checked against it by src/tests/fees.test.js.

// Per session: `full` fee, with a sliding scale from `min` up to `full`.
// (Source: practice fee schedule, Oct 2026.)
export const FEES = {
  supervised: {
    individual: { full: 150, min: 75 },
    couples: { full: 200, min: 100 },
  },
  rynClark: {
    individual: { full: 200, min: 75 },
    couples: { full: 250, min: 100 },
  },
  cameron: {
    individual: { full: 250, min: 150 },
    couples: { full: 350, min: 200 },
  },
};

const LICENSED_TIERS = [FEES.rynClark, FEES.cameron];
const SUPERVISED_TIERS = [FEES.supervised];

// For a group of fee tiers: the span of full fees, and the span of sliding-scale
// rates (lowest floor to highest full fee).
function group(tiers, format) {
  const fulls = tiers.map((t) => t[format].full);
  const mins = tiers.map((t) => t[format].min);
  return {
    full: [Math.min(...fulls), Math.max(...fulls)],
    sliding: [Math.min(...mins), Math.max(...fulls)],
  };
}

export const PUBLISHED_FEES = {
  licensedIndividual: group(LICENSED_TIERS, 'individual'),
  licensedCouples: group(LICENSED_TIERS, 'couples'),
  supervisedIndividual: group(SUPERVISED_TIERS, 'individual'),
  supervisedCouples: group(SUPERVISED_TIERS, 'couples'),
};

const allTiers = Object.values(FEES).flatMap((t) => [t.individual, t.couples]);
// Lowest sliding-scale rate to highest full fee, across everything we offer.
export const OVERALL_RANGE = [Math.min(...allTiers.map((f) => f.min)), Math.max(...allTiers.map((f) => f.full))];

// "$75–$150", or "$150" when both ends match.
export function money([low, high]) {
  return low === high ? `$${low}` : `$${low}–$${high}`;
}

const LI = PUBLISHED_FEES.licensedIndividual;
const LC = PUBLISHED_FEES.licensedCouples;
const SI = PUBLISHED_FEES.supervisedIndividual;
const SC = PUBLISHED_FEES.supervisedCouples;

// Standard published sentences. Use these instead of writing fees by hand.
export const FULL_FEE_TEXT =
  `Full private-pay fees are ${money(LI.full)} per session for individual therapy with a licensed clinician ` +
  `and ${money(SI.full)} with a supervised clinician; couples and relationship therapy is ${money(LC.full)} ` +
  `with a licensed clinician and ${money(SC.full)} with a supervised clinician.`;

export const SLIDING_SCALE_TEXT =
  `Sliding-scale rates are available with every clinician and set with your circumstances in mind: ` +
  `${money(LI.sliding)} per individual session with a licensed clinician (${money(SI.sliding)} with a supervised clinician), ` +
  `and ${money(LC.sliding)} per couples session with a licensed clinician (${money(SC.sliding)} with a supervised clinician).`;

export const SUPERVISED_COST_TEXT =
  `With a supervised clinician, individual sessions are ${money(SI.full)} at full fee, with sliding-scale rates of ` +
  `${money(SI.sliding)}; couples sessions are ${money(SC.full)}, with sliding-scale rates of ${money(SC.sliding)}.`;

// Rows for the /insurance-fees price list and its OfferCatalog structured data.
// `rate` is the full fee; `sliding` is the published sliding-scale range;
// min/maxPrice span the lowest sliding rate to the highest full fee.
export const PRIVATE_PAY_ROWS = [
  {
    service: 'Individual Therapy (Licensed)',
    rate: money(LI.full),
    sliding: money(LI.sliding),
    minPrice: LI.sliding[0],
    maxPrice: LI.full[1],
    servicePath: '/individual-therapy',
    description: `Private-pay individual therapy with a licensed clinician: ${money(LI.full)} full fee, sliding scale ${money(LI.sliding)} depending on clinician.`,
  },
  {
    service: 'Couples Therapy (Licensed)',
    rate: money(LC.full),
    sliding: money(LC.sliding),
    minPrice: LC.sliding[0],
    maxPrice: LC.full[1],
    servicePath: '/couples-relationship-therapy',
    description: `Private-pay couples and relationship therapy with a licensed clinician: ${money(LC.full)} full fee, sliding scale ${money(LC.sliding)} depending on clinician.`,
  },
  {
    service: 'Supervised Clinician — Individual',
    rate: money(SI.full),
    sliding: money(SI.sliding),
    minPrice: SI.sliding[0],
    maxPrice: SI.full[1],
    servicePath: '/start-therapy-sooner',
    description: `Individual therapy with a supervised clinician: ${money(SI.full)} full fee, sliding scale ${money(SI.sliding)}.`,
  },
  {
    service: 'Supervised Clinician — Couples',
    rate: money(SC.full),
    sliding: money(SC.sliding),
    minPrice: SC.sliding[0],
    maxPrice: SC.full[1],
    servicePath: '/start-therapy-sooner',
    description: `Couples therapy with a supervised clinician: ${money(SC.full)} full fee, sliding scale ${money(SC.sliding)}.`,
  },
];
